import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TopBarActions } from "src/redux/TopBar/actions";
import { DashboardActions } from "src/redux/Dashboard/actions";
import { DashboardSelectors } from "src/redux/Dashboard/selectors";
import { MdSelectors } from "src/redux/MarketData/selectors";
import styles from "./styles.module.scss";

const VIEWS = { DASHBOARD: "dashboard" };

const formatKrw = (value) => {
    if (value >= 1e12) return (value / 1e12).toFixed(1) + "T";
    if (value >= 1e9) return (value / 1e9).toFixed(1) + "B";
    if (value >= 1e6) return (value / 1e6).toFixed(1) + "M";
    return Math.round(value).toLocaleString();
};

/**
 * Convert a list of per-currency volumes to a single KRW total.
 * Exchange rates are in the format { USDKRW: 1350, EURKRW: 1480, ... }
 * derived from USD-based rates (USDKRW, USDEUR) via cross calculation.
 */
const toKrw = (volumes, exRates) => {
    if (!volumes || !Array.isArray(volumes)) return 0;
    let total = 0;
    for (const { currency, volume } of volumes) {
        if (!currency || currency === "KRW") {
            total += volume;
        } else if (currency === "USD") {
            // Direct USD -> KRW conversion
            const krwRate = exRates["USDKRW"];
            total += krwRate ? volume * krwRate : volume;
        } else {
            // Convert via USD cross rate: currency -> USD -> KRW
            // exRates has USD-based pairs (e.g. USDEUR = 0.92 means 1 USD = 0.92 EUR)
            const usdRate = exRates["USD" + currency]; // e.g. USDEUR = 0.92
            const krwRate = exRates["USDKRW"]; // e.g. USDKRW = 1350
            if (usdRate && krwRate) {
                // volume is in `currency`. Divide by usdRate to get USD, multiply by krwRate to get KRW.
                total += (volume / usdRate) * krwRate;
            } else {
                // No rate available, show unconverted (best effort)
                total += volume;
            }
        }
    }
    return total;
};

const Dashboard = () => {
    const dispatch = useDispatch();
    const summary = useSelector(DashboardSelectors.Summary);
    const marketBreakdown = useSelector(DashboardSelectors.MarketBreakdown);
    const loading = useSelector(DashboardSelectors.Loading);
    const error = useSelector(DashboardSelectors.Error);
    const exRates = useSelector(MdSelectors.ExRates);

    useEffect(() => {
        dispatch(TopBarActions.SetView(VIEWS.DASHBOARD));
        dispatch(DashboardActions.FetchAll());
    }, [dispatch]);

    const totalVolumeKrw = toKrw(summary.volumes, exRates);

    return (
        <div className={styles.dashboard}>
            <div className={styles.header}>
                <div className={styles.title}>Statistics</div>
            </div>

            {error && (
                <div className={styles.error}>{error}</div>
            )}

            <div className={styles.cards}>
                <div className={styles.card}>
                    <div className={styles.cardLabel}>Total Volume (KRW)</div>
                    <div className={styles.cardValue}>
                        {loading ? "-" : formatKrw(totalVolumeKrw)}
                    </div>
                </div>
                <div className={styles.card}>
                    <div className={styles.cardLabel}>Transactions</div>
                    <div className={styles.cardValue}>
                        {loading ? "-" : (summary.total_transactions || 0).toLocaleString()}
                    </div>
                </div>
            </div>

            <div className={styles.section}>
                <div className={styles.sectionTitle}>Market Breakdown</div>
                {(!marketBreakdown || marketBreakdown.length === 0) && !loading && (
                    <div className={styles.empty}>No trading activity</div>
                )}
                {marketBreakdown && marketBreakdown.length > 0 && (
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Market</th>
                                <th>Volume (KRW)</th>
                                <th>Transactions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {marketBreakdown.map((entry) => (
                                <tr key={entry.market}>
                                    <td>{entry.market}</td>
                                    <td>{formatKrw(toKrw(entry.volumes, exRates))}</td>
                                    <td>{(entry.transaction_count || 0).toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default Dashboard;
